import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Save, Droplets, Gauge } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("nyla_admin_token")}` });

const fmt = (v, digits = 2) =>
  Number(v || 0).toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });

export const CounterSettings = ({ onUnauthorized }) => {
  const [settings, setSettings] = useState(null);
  const [rate, setRate] = useState("");
  const [currentInput, setCurrentInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    axios
      .get(`${API}/admin/counter-settings`, { headers: auth() })
      .then((r) => {
        setSettings(r.data);
        setRate(String(r.data.litres_per_day ?? 3000));
      })
      .catch((e) => {
        if (e.response?.status === 401) onUnauthorized();
        else toast.error("Could not load counter settings");
      });
  }, [onUnauthorized]);

  // Tick every second so the "current value" preview stays live for the admin.
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const liveValue = useMemo(() => {
    if (!settings) return 0;
    const baselineMs = new Date(settings.baseline_at).getTime();
    const perMs = settings.litres_per_day / (24 * 60 * 60 * 1000);
    return Math.max(0, settings.baseline_litres + (now - baselineMs) * perMs);
  }, [settings, now]);

  const save = async (payload) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/counter-settings`, payload, {
        headers: auth(),
      });
      setSettings(data);
      setRate(String(data.litres_per_day));
      setCurrentInput("");
      toast.success("Counter updated");
    } catch (e) {
      if (e.response?.status === 401) onUnauthorized();
      else toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const saveRate = () => {
    const v = parseFloat(rate);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid positive number");
    save({ litres_per_day: v });
  };

  const saveCurrent = () => {
    const v = parseFloat(currentInput);
    if (Number.isNaN(v) || v < 0) return toast.error("Enter a valid positive number");
    save({ current_value: v });
  };

  if (!settings) return <p className="text-sm text-[#86868B]">Loading…</p>;

  return (
    <div data-testid="counter-settings">
      <div className="mb-8">
        <h1 className="font-display text-3xl font-bold tracking-tight mb-2">Water Counter</h1>
        <p className="text-sm text-[#86868B]">
          Controls the live "Nyla is proud to have saved…" ribbon at the top of the home page.
        </p>
      </div>

      {/* Live preview */}
      <div className="bg-black text-white rounded-3xl p-8 mb-8 relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-60"
          style={{
            background:
              "radial-gradient(ellipse at 50% 60%, rgba(74,222,128,0.18) 0%, transparent 65%)",
          }}
        />
        <p className="relative text-[10px] tracking-[0.25em] uppercase font-semibold text-[#4ADE80]/80 mb-3">
          Live preview
        </p>
        <p
          data-testid="counter-preview-value"
          className="relative font-display font-bold tabular-nums text-4xl md:text-5xl tracking-tight"
        >
          {fmt(liveValue)} <span className="text-lg text-white/60 font-medium">L</span>
        </p>
        <p className="relative mt-2 text-xs text-white/60">
          Baseline of <span className="text-white/80 font-medium">{fmt(settings.baseline_litres)} L</span>{" "}
          set on{" "}
          <span className="text-white/80">
            {new Date(settings.baseline_at).toLocaleString("en-US", {
              dateStyle: "medium",
              timeStyle: "short",
            })}
          </span>{" "}
          · Ticking at{" "}
          <span className="text-white/80 font-medium">{fmt(settings.litres_per_day, 0)} L/day</span>
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Set current value */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="flex items-center gap-2 mb-1">
            <Droplets className="w-4 h-4 text-[#2C3E35]" strokeWidth={1.7} />
            <h2 className="font-display text-lg font-bold tracking-tight">Set counter value</h2>
          </div>
          <p className="text-xs text-[#86868B] mb-4">
            Rebase the counter to any number. It will start ticking from this value{" "}
            <span className="font-medium text-[#1D1D1F]">right now</span>.
          </p>
          <div className="flex items-center gap-2">
            <input
              data-testid="counter-current-input"
              type="number"
              step="0.01"
              min="0"
              value={currentInput}
              onChange={(e) => setCurrentInput(e.target.value)}
              placeholder={fmt(liveValue)}
              className="flex-1 bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2C3E35]"
            />
            <button
              data-testid="counter-current-save"
              onClick={saveCurrent}
              disabled={saving || !currentInput}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-xs font-medium hover:bg-black active:scale-95 transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
          <p className="mt-3 text-[11px] text-[#86868B]">
            Tip: use this after a big milestone (e.g. new manufacturing partner) to sync the
            public number with reality.
          </p>
        </div>

        {/* Set daily rate */}
        <div className="bg-white rounded-3xl p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
          <div className="flex items-center gap-2 mb-1">
            <Gauge className="w-4 h-4 text-[#2C3E35]" strokeWidth={1.7} />
            <h2 className="font-display text-lg font-bold tracking-tight">Litres per day</h2>
          </div>
          <p className="text-xs text-[#86868B] mb-4">
            Daily sales rate — how fast the counter climbs. Currently{" "}
            <span className="font-medium text-[#1D1D1F]">{fmt(settings.litres_per_day, 0)} L/day</span>.
          </p>
          <div className="flex items-center gap-2">
            <input
              data-testid="counter-rate-input"
              type="number"
              step="1"
              min="0"
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              className="flex-1 bg-[#F5F5F7] border border-[#D2D2D7] rounded-2xl px-4 py-2.5 text-sm focus:outline-none focus:border-[#2C3E35]"
            />
            <button
              data-testid="counter-rate-save"
              onClick={saveRate}
              disabled={saving || rate === String(settings.litres_per_day)}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-[#1D1D1F] text-white text-xs font-medium hover:bg-black active:scale-95 transition-[background-color,transform] duration-200 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save
            </button>
          </div>
          <p className="mt-3 text-[11px] text-[#86868B]">
            Per-second increment ≈{" "}
            <span className="font-medium text-[#1D1D1F]">
              {fmt((parseFloat(rate) || 0) / 86400, 4)} L/s
            </span>
          </p>
        </div>
      </div>
    </div>
  );
};
